import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { useTabIndicator } from 'src/hooks/useTabIndicator'
import { ContractorTab } from './enums'
import { Typography } from '@mui/material'
import { colors } from 'src/theme'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'

interface Props {
  selectedTab: ContractorTab
  isSubcontractor?: boolean
  onClick(tab: ContractorTab): void
}

const ContractorDetailsTabsComponent = ({ selectedTab, isSubcontractor, onClick }: Props) => {
  const { containerRef, elementRefs, indicatorStyle } = useTabIndicator<ContractorTab>(selectedTab)

  const { hasPermission } = useCheckPermissions()

  const tabs = useMemo((): { key: ContractorTab; title: string }[] => {
    return [
      { key: ContractorTab.Onboarding, title: 'Onboarding steps' },
      { key: ContractorTab.Addons, title: 'Attributes' },
      ...(hasPermission(UserPermission.OnboardingCreate)
        ? [{ key: ContractorTab.Activities, title: 'Activities' }]
        : []),
      ...(isSubcontractor || !hasPermission(UserPermission.ContractorNotesView)
        ? []
        : [{ key: ContractorTab.Notes, title: 'Notes' }]),
      ...(!hasPermission(UserPermission.ContractorFilesView)
        ? []
        : [{ key: ContractorTab.Files, title: 'Files' }]),
    ]
  }, [isSubcontractor, hasPermission])

  return (
    <div
      ref={containerRef}
      css={tw`relative bg-white border-0 border-b border-solid border-[#EDEDED]`}
    >
      <div css={tw`flex w-full overflow-hidden`}>
        {tabs.map((tab) => {
          const isSelected = selectedTab === tab.key

          return (
            <div
              key={tab.key}
              onClick={() => onClick(tab.key)}
              css={tw`pt-4 px-4 pb-3 cursor-pointer transition-colors duration-300`}
            >
              <Typography
                variant={isSelected ? 'h6' : 'subtitle1'}
                ref={(el) => (elementRefs.current[tab.key] = el)}
                color={isSelected ? colors.BASICS_BLACK : colors.SHADES_GREY_500}
              >
                {tab.title}
              </Typography>
            </div>
          )
        })}
      </div>
      <div
        css={[
          tw`absolute bottom-0 h-[3px] bg-black rounded-tl-[8px] rounded-tr-[8px] transition-all duration-300`,
          { left: indicatorStyle.left, width: indicatorStyle.width },
        ]}
      />
    </div>
  )
}

export const ContractorDetailsTabs = memo(ContractorDetailsTabsComponent)
